<template lang='pug'>
.text-input(
	:class='{\
		"empty"      : value === "",\
		"show-label" : value !== "" || hasErrors,\
		"has-error"  : ( hasErrors !== "" ),\
		focused,\
	}'
	@click='focusTextarea'
)
	.minimal-label(v-if='options.minimalLabel || displayError')
		span.highlight-text {{ options.minimalLabel }}
		span.text-error(v-if='hasErrors')  - {{ displayError }}
	.text-wrapper
		textarea(
			ref='textarea'
			v-model='localValue'
			:placeholder='options.placeholder || "Type here"'
			:class='options.classList'

			@input='resizeTextarea'
		)
		.characters-remaining(v-if='options.maxCharacters')
			| Characters remaining:
			span.highlight-text  {{ charactersRemaining }}
</template>

<script>
export default {

	name : 'text-input',

	props : {
		startingValue : {
			default : () => ''
		},
		options : {
			type : Object,
		},
		fields : {
			type : Object,
		},
		errors : {
			type : [Object, String, Boolean],
		},
		value : {
			default : () => '',
		},
		parentVal : {
			default : () => {},
		},
	},

	data : () => ( {
		focused      : false,
		localValue   : '',
		displayError : '',
	} ),

	computed : {

		charactersRemaining() {
			const { maxCharacters } = this.options;

			if ( !maxCharacters ) {
				return '';
			}

			return maxCharacters - this.localValue.length;
		},

		hasErrors() {
			return typeof this.displayError === 'string' && !!this.displayError;
		},

	},

	created() {
		this.localValue = this.value ? this.value : '';
	},

	mounted() {
		this.$nextTick( () => this.resizeTextarea() );
	},

	watch : {

		errors( e ) {
			this.displayError = e;
		},

		localValue( value ) {
			this.displayError = '';

			const { maxCharacters } = this.options;
			if ( maxCharacters && value.length > maxCharacters ) {
				const truncatedValue = value.substr( 0, maxCharacters );

				// this retriggers this function.
				// no emission necessary here
				this.localValue = truncatedValue;
				return;
			}

			this.$emit( 'input', value );
		},

		value( val ) {

			// without this line, you trigger an infinite loop
			// of emitting, hearing a change, emitting, hearing a change,
			// forever
			if ( val !== this.localValue ) {
				this.localValue = val;

				this.$nextTick( () => this.resizeTextarea() );
			}

		}

	},

	methods : {

		resizeTextarea() {

			const { textarea } = this.$refs;

			if ( !textarea ) {
				return;
			}

			let { height } = textarea.getBoundingClientRect();

			while ( height === textarea.scrollHeight ) {
				height -= 5;
				textarea.style.height = `${height}px`;
			}

			textarea.style.height = `${textarea.scrollHeight}px`;

		},

		focusTextarea() {
			this.$refs.textarea.focus();
		}

	}

};
</script>

<style lang='scss'>
@import '@/assets/scss/_variables.scss';

.text-input {
	position: relative;

	&.show-label {

		.text-wrapper {
			padding-top: 18px;
		}

		.minimal-label {
			transform: translate3d(0,-7px,0);
			opacity: 1;
			z-index: 2;
		}

		textarea {
			transform: translate3d(0,7px,0);
		}
	}

	.text-error {
		color: $red;
	}

	.minimal-label {
		position: absolute;
		top: 15px;
		left: 15px;
		transition: opacity 0.2s ease, transform 0.2s ease;
		opacity: 0;
		transform: translate3d(0,0,0);
		font-size: 12px;
		width: 100%;

		span {
			color: $color-primary;

			&.error {
				color: $error;
			}
		}
	}

	.text-wrapper {
		padding: 15px;
		background: white;
		box-shadow: $inputBoxShadow;
		border-radius: 15px;
		width: 100%;

		textarea {
			width: 100%;
			resize: none;
			font-size: 16px;
			padding: 0;
			color: $input-color;
			transition: transform 0.2s ease;
			display: block;
		}
	}

	.characters-remaining {
		font-size: 12px;
		color: $input-color;
		margin-top: 5px;
		text-align: right;
		transform: translateY(3px);

		span {
			color: $color-primary;
		}
	}
}
</style>
